import React from 'react';
import { Flex, FabIconButton } from '@withjoy/joykit';
import { PauseFill, PlayFill } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { animationTransition } from '@shared/utils/animationTransition';
import type { MusicToggleInset } from './DigitalCardMusicToggle.types';

const BUTTON_SIZE = { _: '40px', sm2: '36px' };
/** Lets the card finish sliding out of the envelope before the button fades in. */
const REVEAL_DELAY = '1000ms';
/** Above the envelope's pieces (up to 401 in `premiumEnvelope.styles`), below its confetti and the customizer's CTA bar (500). */
const Z_INDEX = 450;

const DEFAULT_MUSIC_TOGGLE_INSET: MusicToggleInset = { bottom: 6 };

type DigitalCardMusicToggleProps = Readonly<{
  /** Shown once the envelope is open, so the reveal stays the first thing the recipient sees. */
  isVisible: boolean;
  isPlaying: boolean;
  onToggle: () => void;
  inset?: MusicToggleInset;
}>;

/**
 * Play/pause for a digital card's music, pinned to the bottom centre of the viewport so it stays in reach while the
 * recipient scrolls the card.
 */
export const DigitalCardMusicToggle = ({ isVisible, isPlaying, onToggle, inset = DEFAULT_MUSIC_TOGGLE_INSET }: DigitalCardMusicToggleProps) => {
  const { tq } = useTranslation('cardGuest');

  return (
    <Flex
      position="fixed"
      left={0}
      right={inset.right ?? 0}
      bottom={inset.bottom}
      zIndex={Z_INDEX}
      justifyContent="center"
      // The strip spans the width; only the button should take clicks.
      pointerEvents="none"
      opacity={isVisible ? 1 : 0}
      visibility={isVisible ? 'visible' : 'hidden'}
      transition={animationTransition('opacity', 'visibility', { duration: '300ms', delay: isVisible ? REVEAL_DELAY : '0s' })}
    >
      <FabIconButton
        variant="ghostShadow"
        aria-label={isPlaying ? tq`Pause music` : tq`Play music`}
        height={BUTTON_SIZE}
        minWidth={BUTTON_SIZE}
        width={BUTTON_SIZE}
        pointerEvents="auto"
        onClick={onToggle}
      >
        {isPlaying ? <PauseFill size={16} /> : <PlayFill size={16} />}
      </FabIconButton>
    </Flex>
  );
};
